import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountDocumentDetailResponse,
  AccountDocumentForm,
  AccountDocumentFormSchema,
  IAccountRespSchema,
  SigningOrderEnum,
} from '@monetize/types/app';
import { toDateTimeShort } from '@monetize/utils/core';
import { parseISO } from 'date-fns/parseISO';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUpdateAccountDocument } from '../../../../api/accountsService';
import {
  MFormField,
  MInput,
  MRadio,
  MRadioGroup,
  MText,
} from '../../../../components/Monetize';
import {
  MBox,
  MGrid,
  MGridItem,
  MStack,
} from '../../../../components/Monetize/chakra';
import { SectionHeader } from './SectionHeader';

interface AccountDocumentDetailSectionProps {
  accountDocument: AccountDocumentDetailResponse;
  isReadOnly?: boolean;
  account: IAccountRespSchema;
  refetchAccountDocument: () => void;
}

export const AccountDocumentDetailSection = ({
  accountDocument,
  isReadOnly,
  account,
  refetchAccountDocument,
}: AccountDocumentDetailSectionProps) => {
  const { accountId } = accountDocument;

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(AccountDocumentFormSchema),
    defaultValues: {},
    mode: 'onChange',
  });

  const {
    mutateAsync: doUpdateAccountDocument,
    isPending: isUpdatingAccountDocument,
  } = useUpdateAccountDocument({
    onSuccess: async () => {
      await refetchAccountDocument();
      setIsEditMode(false);
    },
  });

  useEffect(() => {
    reset(accountDocument);
  }, [accountDocument, reset]);

  const onSubmit = async (payload: AccountDocumentForm) => {
    doUpdateAccountDocument({
      accountId,
      accountDocumentId: accountDocument.id,
      payload,
    });
  };

  const isReadOnlyProps = {
    isDisabled: !isEditMode,
    disabled: !isEditMode,
    isReadOnly: !isEditMode,
    variant: !isEditMode ? 'readonly' : 'outline',
  } as const;

  const signOptions = useMemo(() => {
    return [
      {
        label: (
          <MText>
            Send to{' '}
            {account?.accountName ? (
              <strong>{account?.accountName}</strong>
            ) : (
              'external account contact'
            )}{' '}
            first
          </MText>
        ),
        value: SigningOrderEnum.EXTERNAL_FIRST,
      },
      {
        label: <MText>Send to internal contact first</MText>,
        value: SigningOrderEnum.INTERNAL_FIRST,
      },
      {
        label: <MText>Send to both contacts at the same time</MText>,
        value: SigningOrderEnum.INSTANTANEOUS,
      },
      {
        label: (
          <MText>
            Only require signature from customer{' '}
            {account?.accountName ? (
              <strong>{account?.accountName}</strong>
            ) : (
              'customer'
            )}
          </MText>
        ),
        value: SigningOrderEnum.EXTERNAL_ONLY,
      },
    ];
  }, [account?.accountName]);

  return (
    <MBox width="100%" flex="1">
      <form
        id="account-document-form"
        data-testid="account-document-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <SectionHeader
          label="Details"
          isEditMode={isEditMode}
          isLoading={isUpdatingAccountDocument}
          onEdit={() => {
            setIsEditMode((prevState) => !prevState);
          }}
          onSave={() => {
            handleSubmit(onSubmit)();
          }}
          canEdit={!isReadOnly}
        />
        <MGrid templateColumns="repeat(6, 1fr)" gap={4}>
          <MGridItem colSpan={3}>
            <MFormField label="Name" error={errors?.name} isRequired>
              <Controller
                name={`name`}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <MInput value={value || ''} {...field} {...isReadOnlyProps} />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={3}>
            <MFormField label="Description" error={errors?.description}>
              <Controller
                name={`description`}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <MInput value={value || ''} {...field} {...isReadOnlyProps} />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={6}>
            <MFormField
              label="Signing Order"
              error={errors?.signingOrder}
              isRequired
            >
              {isEditMode ? (
                <Controller
                  name="signingOrder"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MRadioGroup
                      onChange={(val) => onChange(val as SigningOrderEnum)}
                      value={value || SigningOrderEnum.EXTERNAL_FIRST}
                      {...rest}
                      isDisabled={isReadOnlyProps.isDisabled}
                    >
                      <MStack columnGap={5}>
                        {signOptions.map((option) => (
                          <MRadio key={option.value} value={option.value}>
                            {option.label}
                          </MRadio>
                        ))}
                      </MStack>
                    </MRadioGroup>
                  )}
                />
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {
                    signOptions.find(
                      (option) => option.value === accountDocument.signingOrder,
                    )?.label
                  }
                </>
              )}
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={3}>
            <MFormField label="Sent At">
              {accountDocument.sentAt ? (
                <MText>
                  {toDateTimeShort(
                    parseISO(accountDocument.sentAt),
                    'userTimezone',
                  )}
                </MText>
              ) : (
                <MText fontStyle="italic">Not sent yet</MText>
              )}
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={3}>
            <MFormField label="Signed At">
              {accountDocument.signedAt ? (
                <MText>
                  {toDateTimeShort(
                    parseISO(accountDocument.signedAt),
                    'userTimezone',
                  )}
                </MText>
              ) : (
                <MText fontStyle="italic">Not signed yet</MText>
              )}
            </MFormField>
          </MGridItem>
        </MGrid>
      </form>
    </MBox>
  );
};
